import * as THREE from "three";
import type { DishKind } from "../game/dishes";
import type { Box, Station } from "../game/kitchen";
import type { Outside } from "./boardwalk";

// Shared pieces for building rooms: materials, boxes by their extents, and
// what a room hands back to `KitchenScene` to drive.

export const steel = () => new THREE.MeshStandardMaterial({ color: 0xc9d0d6, metalness: 0.75, roughness: 0.32 });
export const paint = (color: number, roughness = 0.8) => new THREE.MeshStandardMaterial({ color, roughness });

export function box(w: number, h: number, d: number, mat: THREE.Material | THREE.Material[], x: number, y: number, z: number, parent: THREE.Object3D): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  parent.add(m);
  return m;
}

/** A box spanning [x0, x1] × [y0, y1] × [z0, z1]. */
export function span(x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, mat: THREE.Material | THREE.Material[], parent: THREE.Object3D): THREE.Mesh {
  return box(x1 - x0, y1 - y0, z1 - z0, mat, (x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2, parent);
}

export function spanBox(b: Box, mat: THREE.Material | THREE.Material[], parent: THREE.Object3D): THREE.Mesh {
  return span(b.min[0], b.max[0], b.min[1], b.max[1], b.min[2], b.max[2], mat, parent);
}

/** Where a clean dish sits on the shelf: its position, upside down or not, and its turn. */
export interface ShelfSpot {
  pos: THREE.Vector3;
  flip: boolean;
  turn: number;
}

/** A room's fixed parts, and the handles `KitchenScene` moves or places things by. */
export interface Room {
  group: THREE.Group;
  /** The machine's hood, which lifts to open: 0 shut, 1 open. */
  hood: THREE.Object3D;
  /** The machine's status light. */
  lamp: THREE.MeshStandardMaterial;
  machineBox: Box;
  hourHand: THREE.Object3D;
  minuteHand: THREE.Object3D;
  /** Materials that glow when you aim at their station. */
  highlight: Partial<Record<Station, THREE.MeshStandardMaterial[]>>;
  /** Where the sink's dish sits, the drainboard rack, the rack inside the machine, the ticket rail's middle. */
  anchors: { sink: THREE.Vector3; rack: THREE.Vector3; machineRack: THREE.Vector3; ticketRail: THREE.Vector3 };
  /** Where the `i`th clean dish of a kind goes on the shelf. */
  shelfSpot(kind: DishKind, i: number): ShelfSpot;
  /** The sun or ceiling light: where it shines from and at. */
  keyLight: { from: THREE.Vector3; at: THREE.Vector3 };
  /** The warm light through the pass or window. */
  passLight: THREE.Vector3;
  /** What's out of the window, when it's more than a backdrop: it sets the fog and the view distance, and moves. */
  outside?: Outside;
  /** At a coffee bar: where shot glasses sit under the group heads (and the heads themselves), the pitcher on the wand, the cup on the bar. */
  cafe?: { groups: THREE.Vector3[]; grouphead: THREE.Vector3[]; wand: THREE.Vector3; bar: THREE.Vector3 };
  /** At a restaurant where you cook: the spots food sits on. */
  cooking?: {
    griddle: THREE.Vector3[];
    fryer: THREE.Vector3[];
    /** The assembly board's basket spots, in the state's order. */
    board: THREE.Vector3[];
    /** The order-up bell on the window ledge, which dips when rung. */
    bell: THREE.Object3D;
    /** Where drinks wait on the window's ledge, in order. */
    ledge: THREE.Vector3[];
  };
}
